import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import MapLeaflet from "@/components/MapLeaflet";
import { Seo } from "@/components/Seo";
import { TypeFilterChips, CommitmentChips, QuickWinChips } from "@/components/opportunities/FilterChips";
import { SearchSortRow, SmartSearchHint, GeoPanel } from "@/components/opportunities/SearchControls";
import { OpportunityCard } from "@/components/opportunities/OpportunityCard";
import { SearchX, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Browse page: state + data-fetching live here; presentational pieces are in
// components/opportunities/* (Phase 15 component split — behaviour unchanged).
export default function Opportunities() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [orgRatings, setOrgRatings] = useState({});

  // Phase 8: batch-fetch org rating aggregates for the visible cards.
  useEffect(() => {
    const ids = [...new Set(items.map(o => o.org_user_id).filter(Boolean))];
    if (ids.length === 0) return;
    api.orgRatings(ids)
      .then((d) => setOrgRatings(prev => ({ ...prev, ...d.ratings })))
      .catch(() => {});
  }, [items]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");
  const [qDebounced, setQDebounced] = useState(sp.get("q") || "");
  const [value, setValue] = useState(sp.get("value") || "all");
  const [skill, setSkill] = useState(sp.get("skill") || "all");
  const [remote, setRemote] = useState(sp.get("remote") === "1" || sp.get("remote") === "true");
  const [hasCapacity, setHasCapacity] = useState(sp.get("has_capacity") === "true");
  const [dbs, setDbs] = useState(sp.get("dbs") || "any"); // B4: any | no | only
  const [type, setType] = useState(sp.get("type") || "all");
  const initialCommit = sp.get("commitment_type") ? new Set(sp.get("commitment_type").split(",")) : new Set();
  const [commitment, setCommitment] = useState(initialCommit);
  const [quickWin, setQuickWin] = useState(sp.get("quick_win") === "true");
  const [thisWeek, setThisWeek] = useState(sp.get("this_week") === "true");
  const [sort, setSort] = useState(sp.get("sort") || "");
  const [center, setCenter] = useState(null); // {lat, lng, label}
  const [radius, setRadius] = useState(25);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // sync URL for shareable / reload-safe state. selfWrite tracks what WE wrote
  // so the URL→state effect below can tell our own writes apart from external
  // navigation (nav links, back/forward) and never loops or clobbers typing.
  const selfWrite = useRef(null);
  useEffect(() => {
    const next = new URLSearchParams();
    if (qDebounced) next.set("q", qDebounced);
    if (value !== "all") next.set("value", value);
    if (skill !== "all") next.set("skill", skill);
    if (remote) next.set("remote", "true");
    if (hasCapacity) next.set("has_capacity", "true");
    if (dbs !== "any") next.set("dbs", dbs);
    if (type !== "all") next.set("type", type);
    if (commitment.size) next.set("commitment_type", [...commitment].join(","));
    if (quickWin) next.set("quick_win", "true");
    if (thisWeek) next.set("this_week", "true");
    if (sort) next.set("sort", sort);
    selfWrite.current = next.toString();
    setSp(next, { replace: true });
  }, [qDebounced, value, skill, remote, hasCapacity, dbs, type, commitment, quickWin, thisWeek, sort, setSp]);

  // URL → state: nav links (e.g. "Browse help requests" → ?type=help) change
  // only the query string while we stay mounted — adopt the new URL as truth
  // whenever it wasn't one of our own writes, so filters always follow the link.
  useEffect(() => {
    if (sp.toString() === selfWrite.current) return;
    selfWrite.current = sp.toString();
    const nq = sp.get("q") || "";
    setQ(nq); setQDebounced(nq);
    setValue(sp.get("value") || "all");
    setSkill(sp.get("skill") || "all");
    setRemote(sp.get("remote") === "1" || sp.get("remote") === "true");
    setHasCapacity(sp.get("has_capacity") === "true");
    setDbs(sp.get("dbs") || "any");
    setType(sp.get("type") || "all");
    setCommitment(new Set(sp.get("commitment_type") ? sp.get("commitment_type").split(",") : []));
    setSort(sp.get("sort") || "");
  }, [sp]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 50 };
    if (qDebounced) params.q = qDebounced;
    if (value && value !== "all") params.value = value;
    if (skill && skill !== "all") params.skill = skill;
    if (remote) params.remote = true;
    if (hasCapacity) params.has_capacity = true;
    if (dbs === "no") params.dbs_required = false;
    if (dbs === "only") params.dbs_required = true;
    if (type && type !== "all") params.type = type;
    if (commitment.size > 0) params.commitment_type = [...commitment];
    if (quickWin) params.quick_win = true;
    if (thisWeek) params.this_week = true;
    if (sort) params.sort = sort;
    if (center) {
      params.lat = center.lat;
      params.lng = center.lng;
      params.radius_km = radius;
    }
    api.listOpportunities(params)
      .then(d => { setItems(d.items || []); setTotal(d.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [qDebounced, value, skill, remote, hasCapacity, dbs, type, commitment, quickWin, thisWeek, sort, center, radius]);

  const toggleCommitment = (ct) => {
    setCommitment(prev => {
      const next = new Set(prev);
      if (next.has(ct)) next.delete(ct); else next.add(ct);
      return next;
    });
  };

  const clearFilters = () => {
    setQ(""); setQDebounced("");
    setValue("all"); setSkill("all");
    setRemote(false); setHasCapacity(false);
    setType("all"); setCommitment(new Set());
    setQuickWin(false); setThisWeek(false);
    setSort(""); setCenter(null); setRadius(25);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Current location" });
        toast.success("Filtering near your current location");
      },
      () => toast.error("Couldn't get your location"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const useProfileLocation = () => {
    if (!user?.lat || !user?.lng) return toast.error("Add lat/lng to your profile in onboarding");
    setCenter({ lat: user.lat, lng: user.lng, label: user.location || "Your saved location" });
  };

  const clearGeo = () => setCenter(null);

  const markers = useMemo(() =>
    items.filter(i => i.lat && i.lng).map(i => ({ lat: i.lat, lng: i.lng, title: i.title, subtitle: i.location })),
    [items]
  );

  const mapCenter = useMemo(() =>
    center ? [center.lat, center.lng] : (markers[0] ? [markers[0].lat, markers[0].lng] : [51.5, -0.1]),
    [center, markers]
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Seo title="Volunteer opportunities" description="Browse live volunteering opportunities near you — filter by cause, skills and distance, or explore the map." />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Browse</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Opportunities near you</h1>
        </div>
        {user && user.user_type !== "organisation" && (
          <Link
            to="/help-request/new"
            data-testid="ask-for-help-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-full h-11 px-5 transition-colors"
          >
            <HeartHandshake className="w-4 h-4" /> Ask a neighbour for help
          </Link>
        )}
      </div>

      <TypeFilterChips type={type} onSelect={setType} />
      <QuickWinChips
        quickWin={quickWin} thisWeek={thisWeek}
        onToggleQuickWin={() => setQuickWin(v => !v)}
        onToggleThisWeek={() => setThisWeek(v => !v)}
      />
      <CommitmentChips
        commitment={commitment}
        onToggle={toggleCommitment}
        onClear={() => setCommitment(new Set())}
      />
      <SearchSortRow
        q={q} setQ={setQ} value={value} setValue={setValue}
        skill={skill} setSkill={setSkill} sort={sort} setSort={setSort}
        qDebounced={qDebounced}
      />
      <SmartSearchHint user={user} />
      <GeoPanel
        user={user}
        remote={remote} setRemote={setRemote}
        hasCapacity={hasCapacity} setHasCapacity={setHasCapacity}
        dbs={dbs} setDbs={setDbs}
        center={center} radius={radius} setRadius={setRadius}
        matchingCount={items.length}
        onUseMyLocation={useMyLocation}
        onUseProfileLocation={useProfileLocation}
        onClearGeo={clearGeo}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-4">
          {!loading && items.length === 0 && (
            <div data-testid="opps-empty-state" className="text-center py-16 bg-white border border-dashed border-neutral-300 rounded-2xl">
              <SearchX className="w-10 h-10 text-forest/30 mx-auto" />
              <div className="font-display text-xl text-forest mt-3">No opportunities match your filters</div>
              <p className="text-sm text-neutral-500 mt-1">Try clearing a filter or widening your search radius.</p>
              <button
                data-testid="filter-clear-btn"
                onClick={clearFilters}
                className="mt-4 text-sm px-4 py-2 rounded-full bg-forest text-white hover:bg-forest-hover transition-colors"
              >
                Clear filters
              </button>
              {/* Phase 17-B item 4: Pathways entry point on the empty state */}
              <div className="mt-4">
                <Link to="/pathways" data-testid="opps-empty-pathways-link" className="text-sm font-medium text-forest/70 hover:text-terracotta transition-colors">
                  Not sure where you fit? See the ways to help →
                </Link>
              </div>
            </div>
          )}
          {items.length > 0 && (
            <div className="text-xs text-neutral-500 mb-2" data-testid="opps-result-count">
              Showing {items.length} of {total} opportunities
            </div>
          )}
          {items.map((o, idx) => (
            <OpportunityCard key={o.id} o={o} idx={idx} rating={orgRatings[o.org_user_id]} />
          ))}
        </div>
        <div className="space-y-4 lg:sticky lg:top-24 h-fit">
          <MapLeaflet
            markers={markers}
            center={mapCenter}
            zoom={center ? 9 : 6}
            height={420}
          />
          <div className="text-xs text-neutral-500">
            {user
              ? "Pins show opportunities with mapped locations."
              : <span data-testid="map-approx-hint">Pins show approximate areas — <Link to="/login" className="text-forest font-medium underline">sign in</Link> for exact locations.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
